'use client';

/**
 * Event Card Component
 *
 * Displays concert event with luxury styling, limited ticket counter, and VIP package info
 */

import Link from 'next/link';
import Image from 'next/image';
import { Event } from '@/lib/mock-data';
import { Calendar, MapPin, Ticket, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface EventCardProps {
  event: Event;
  featured?: boolean;
}

export function EventCard({ event, featured = false }: EventCardProps) {
  const isLowAvailability = event.ticketsRemaining < 10;
  const availabilityPercentage = (event.ticketsRemaining / event.totalTickets) * 100;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const getLowestPrice = () => {
    if (event.packages.length === 0) return null;
    const prices = event.packages.map(pkg => pkg.priceUSDT);
    return Math.min(...prices);
  };

  const lowestPrice = getLowestPrice();

  return (
    <div
      className={`group relative overflow-hidden rounded-lg glass-card hover-glow transition-all duration-300 ${
        featured ? 'lg:col-span-2' : ''
      }`}
    >
      {/* Event Image */}
      <div className="relative aspect-video overflow-hidden">
        <Image
          src={event.imageUrl}
          alt={event.eventTitle}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-110"
        />
        {/* Dark overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

        {/* Featured Badge */}
        {featured && (
          <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground border-none shadow-gold">
            Featured Event
          </Badge>
        )}

        {/* Limited Tickets Warning */}
        {isLowAvailability && (
          <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-urgency/90 backdrop-blur-sm">
            <AlertCircle className="h-4 w-4 text-urgency-foreground pulse-urgency" />
            <span className="text-sm font-bold text-urgency-foreground">
              Only {event.ticketsRemaining} Left!
            </span>
          </div>
        )}

        {/* Event Details Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 space-y-3">
          <div>
            <h3 className="text-2xl font-bold text-white mb-1">
              {event.artistName}
            </h3>
            <p className="text-sm text-white/80">{event.eventTitle}</p>
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-white/90">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <span>{formatDate(event.date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              <span>{event.city}, {event.country}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-6 space-y-4">
        {/* Venue */}
        <p className="text-sm text-muted-foreground">{event.venue}</p>

        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2">
          {event.description}
        </p>

        {/* Availability Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Ticket Availability</span>
            <span className={`font-medium ${isLowAvailability ? 'text-urgency' : 'text-success'}`}>
              {event.ticketsRemaining} / {event.totalTickets}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                isLowAvailability ? 'bg-urgency' : 'bg-success'
              }`}
              style={{ width: `${availabilityPercentage}%` }}
            />
          </div>
        </div>

        {/* Price and CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-primary/20">
          <div>
            {lowestPrice && (
              <>
                <p className="text-xs text-muted-foreground mb-1">Starting from</p>
                <p className="text-2xl font-bold text-primary">
                  {lowestPrice.toLocaleString()} <span className="text-lg">USDT</span>
                </p>
              </>
            )}
          </div>
          <Button
            className="bg-gradient-to-r from-primary to-secondary hover:shadow-gold"
            asChild
          >
            <Link href={`/events/${event.id}`}>
              <Ticket className="mr-2 h-4 w-4" />
              View Packages
            </Link>
          </Button>
        </div>

        {/* Package Tiers */}
        <div className="flex flex-wrap gap-2">
          {event.packages.map((pkg) => (
            <Badge
              key={pkg.id}
              variant="outline"
              className="border-primary/30 text-primary"
            >
              {pkg.tier}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}
